/**
 * `backtest` namespace — English canonical source.
 *
 * Owned by the Backtest team (backtest runner and results on the strategy detail
 * page). Keys are dot-free camelCase; every value is the canonical English text.
 * `ko/backtest.ts` mirrors this shape exactly — a missing or misspelled key is a
 * compile error there, so the two catalogs cannot drift. The catalog index picks
 * this namespace up automatically; adding a key never requires editing the index.
 *
 * Glossary constraints (binding): "Backtest" → "백테스트"; Fear & Greed / FGI / bps /
 * CAGR stay English. Metric percent values arrive pre-scaled from the engine
 * (`totalReturnPct` etc. are already ×100); win rate is a 0..1 ratio the UI turns
 * into a percent.
 */
export const backtest = {
  // ── panel chrome ──
  panelTitle: "Backtest",
  panelSubtitle: "Simulate this strategy against historical market data.",

  // ── locked (trading feature gate) ──
  lockedTitle: "Toss connection required",
  lockedDesc:
    "A backtest reads historical market data through your Toss connection. Connect Toss to run backtests.",
  lockedCta: "Connect Toss",

  // ── config form ──
  configTitle: "New backtest",
  periodLabel: "Period",
  period6m: "6M",
  period1y: "1Y",
  period3y: "3Y",
  periodAll: "All",
  periodCustom: "Custom",
  fromLabel: "From",
  toLabel: "To",
  initialCashLabel: "Initial cash",
  currencyLabel: "Currency",
  advancedLabel: "Advanced",
  feeBpsLabel: "Fee (bps)",
  slippageBpsLabel: "Slippage (bps)",
  bpsHint: "1 bp = 0.01%",
  useAdvisorLabel: "Run the AI advisor on every trigger",
  useAdvisorHint: "Calls the model on each trigger — slower, and uses credits.",
  runButton: "Run backtest",
  runningButton: "Queuing…",
  invalidCash: "Enter an amount greater than zero.",
  invalidRange: "The start date must be before the end date.",
  conflictNotice:
    "A backtest is already running for this strategy. Wait for it to finish before starting another.",
  runError: "Could not start the backtest.",

  // ── job status ──
  statusQueued: "Queued",
  statusRunning: "Running",
  runningTitle: "Running backtest…",
  runningDesc: "Fetching history and replaying the strategy. This can take a moment.",
  errorTitle: "Backtest failed",
  detailLoadError: "Could not load this backtest.",
  noResults: "Set a period and run a backtest to see results here.",

  // ── result metrics ──
  resultsTitle: "Results",
  metricInitialCash: "Starting capital",
  metricFinalEquity: "Final equity",
  metricTotalReturn: "Total return",
  metricCagr: "CAGR",
  metricMaxDrawdown: "Max drawdown",
  metricWinRate: "Win rate",
  metricPayoffRatio: "Payoff ratio",
  metricTradeCount: "Trades",
  cagrTooShort: "Under 1 year",
  benchmarkValue: "Benchmark {value}",
  roundTripsCaption: "{count} round trips",

  // ── equity chart ──
  equityTitle: "Equity curve",
  equitySubtitle: "Strategy vs. benchmark",
  legendStrategy: "Strategy",
  legendBenchmark: "Benchmark",
  noEquity: "No equity data for this run.",

  // ── price overlay chart ──
  priceTitle: "Price overlay",
  priceSubtitle: "Rebased to 100 at start — check series to overlay",
  priceFgi: "FGI",
  priceTrades: "Trades",
  priceRebasedNote:
    "Each line is rebased to 100 on its first day, so differently-priced assets compare on one % scale. FGI reads on the right axis (0–100).",
  priceEmpty: "No price series for this run.",
  priceUnchartedTrades: "{count} trades not shown on chart (no price data for {symbols}).",
  priceIndicatorNote:
    "Indicators are computed from the shown window: SMA/EMA overlay the price scale, RSI/ROC/drawdown read on a separate axis, and values near the start are approximate.",

  // ── advisor decisions ──
  advisorTitle: "Advisor decisions",
  advisorEmpty: "The advisor made no decisions in this run.",
  advisorVerdictApprove: "Approve",
  advisorVerdictVeto: "Veto",
  advisorVerdictResize: "Resize",
  advisorResizeMultiplier: "×{value}",
  advisorShowMore: "Show more",
  advisorShowLess: "Show less",

  // ── data coverage ──
  coverageTitle: "Data coverage",
  coverageRange: "Effective range {from} – {to}",
  warnDataStartsLater:
    "Price history starts later than requested; the backtest begins on the first day with data.",
  warnNoData: "No price history was available for the requested range.",
  warnNoFgiData: "No Fear & Greed data for part of this range; rules using FGI were skipped there.",
  warnGeneric: "Coverage warning: {code}",

  // ── trades table ──
  tradesTitle: "Trades",
  tradesEmpty: "This backtest placed no trades.",
  colDate: "Date",
  colSymbol: "Symbol",
  colSide: "Side",
  colQty: "Qty",
  colPrice: "Price",
  colFee: "Fee",
  colRule: "Rule",
  sideBuy: "Buy",
  sideSell: "Sell",
  tradesShown: "Showing {shown} of {total}",
  showAllTrades: "Show all trades",

  // ── recent runs ──
  recentTitle: "Recent backtests",
  recentEmpty: "No backtests yet — run one above.",
  recentError: "Could not load recent backtests.",
  reopenAria: "Open the backtest from {date}",
  periodRange: "{from} – {to}",

  // ── delete a run ──
  deleteRun: "Delete",
  deleteConfirm: "Really delete?",
  deleting: "Deleting…",
  deleteError: "Could not delete this run.",
} as const;

export type BacktestKey = keyof typeof backtest;
